<template>
  <right-side-bar
    fixed
    divider
    prepend
    width="30%"
    drawerClasses="pa-8"
    @toggle-right-bar="closeNavDrawer"
    :is-right-drawer-open="addUserDrawer"
    :title="this.$dictionary.app.manageUsers.addUserForm.title"
  >
    <v-form
      id="add-user-form"
      ref="form"
      v-model="isFormValid"
      @submit.prevent="handleSubmit"
      @keyup.enter="handleSubmit"
      autocomplete="off"
    >
      <v-row>
        <v-col cols="12" class="py-2">
          <div class="pb-6">
            <label for="user-email-address" class="overline font-weight-large">
              {{ emailAddressLabel }}
            </label>
          </div>
          <div>
            <v-text-field
              id="user-email-address"
              v-model="user.email"
              :rules="[
                required('Email address'),
                validateEmail,
                maxLength(50, 'Email'),
              ]"
              label="Email Address"
              class="required"
              outlined
            />

            <template v-if="!userExists">
              <password-input-field
                isRequired
                isHintPersistent
                label="Temporary Password"
                :rules="[
                  isNewPasswordValid,
                  maxLength(50, 'Temporary Password'),
                ]"
                :password="formData.password"
                @update-password="formData.password = $event"/>

              <transition-group name="list" tag="p">
                <template v-for="{ text, isValid } in passwordRules">
                  <v-row :key="text" no-gutters>
                    <v-col cols="1" class="d-flex align-center">
                      <v-icon :color="getIconColor({ text, isValid })" small>
                        {{ isValid ? "mdi-check-circle" : "mdi-close-circle" }}
                      </v-icon>
                    </v-col>
                    <v-col
                      cols="11"
                      :class="!isValid && isNewPasswordDirty ? 'red--text' : ''"
                      class="caption py-1"
                    >
                      {{ text }}
                    </v-col>
                  </v-row>
                </template>
              </transition-group>

              <password-input-field
                isRequired
                isHintPersistent
                label="Confirm Temporary Password"
                :rules="[
                  validateConfirmPassword,
                  isConfirmPasswordSame,
                  maxLength(50, 'Confirm Temporary Password'),
                ]"
                :password="formData.confirmPassword"
                @update-password="formData.confirmPassword = $event"
            /></template>
          </div>
        </v-col>
      </v-row>
      <slot name="roles" />
      <v-row>
        <v-col>
          <v-btn
            type="submit"
            color="primary"
            :loading="isLoading"
            :disabled="!isFormValid"
            class="px-4 button font-weight-bold white-btn-text"
          >
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </right-side-bar>
</template>
<script>
import { mapActions } from "vuex";
import { PasswordValidatorMixin } from "@/mixins";
import { PasswordInputField } from "../shared";
import {
  createUserProfile,
  createUser,
  fetchSignInMethodsForEmail,
  getUsers,
} from "@/services";
import {
  required,
  validateEmail,
  letterOnly,
  maxLength,
} from "@/validators/form-validators";
import { RESPONSE_CODES } from "@/constants";
import { isEmpty, pick, isResponseCode } from "@/utils/common.utils";
import { RightSideBar } from "@/components/drawers";
import { capitalize } from "@/filters";
import { TABLE_PER_PAGES_OPTIONS } from "@/constants";

/**
 * Add or update user form
 * @description Creates  and updates the user
 */
export default {
  name: "AddUserForm",
  /**
   * -------------- Components -------------
   */
  components: {
    RightSideBar,
    PasswordInputField,
  },
  /**
   * -------------- Mixins -------------
   */
  mixins: [PasswordValidatorMixin],
  /**
   * -------------- Props Properties -------------
   */
  props: {
    addUserDrawer: {
      type: Boolean,
      default: false,
    },
    emailAddressLabel: { type: String, default: "USER INFORMATION" },
  },
  /**
   * -------------- Props Properties -------------
   */
  data() {
    return {
      isFormValid: false,
      user: {
        email: "",
        password: "",
        confirmPassword: "",
      },
      userExists: true,
      isLoading: false,
      isVerifiedUser: false,
    };
  },
  /**
   * ---------- Watch properties ------------
   */
  watch: {
    /**
     * Watches user email changes and then resets the view
     */
    "user.email"(val, oldVal) {
      if (val !== oldVal && !this.userExists) {
        this.userExists = true;
      }
    },
  },

  /**
   * ----------Computed properties-------
   */
  computed: {
    /**
     * Error messages
     * @description Fetched from the app dictionary
     */
    displayMessages() {
      const { snackbarTypes } = this.$appConfig.snackbar;
      const { errorMessages } = this.$dictionary.errors.password;
      return { ...errorMessages, snackbarTypes };
    },
    /**
     * Create user request payload
     */
    createUserPayload() {
      const { email } = this.user;

      return {
        email,
        ...pick(this.formData, ["password"]),
      };
    },
  },
  /**
   * -----------Emit Events---------------
   */
  emits: ["close-nav-drawer", "user-added"],

  /**
   * -------------Methods -----------------
   */
  methods: {
    required,
    validateEmail,
    maxLength,
    letterOnly,
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),

    /**
     * Close Nav drawer
     * @event close-nav-drawer
     */
    closeNavDrawer(val = !this.addUserDrawer) {
      this.$emit("close-nav-drawer", val);
    },
    /**
     * Verifies that user profile is present in InboxAware database or not
     * @returns {Boolean} Is user profile present or not.
     */
    async verifyInboxAwareUser(email) {
      try {
        this.isLoading = true;
        const [per_page] = TABLE_PER_PAGES_OPTIONS;
        const payload = { "q[email_cont]": email, per_page, page: 1 };

        const { data } = await getUsers(payload);
        this.isVerifiedUser = !isEmpty(data)
          ? data.some(
              (user) => user.email.toLowerCase() === email.toLowerCase()
            )
          : false;

        // Emits event in parent component to create a new record in parent component
        if (this.isVerifiedUser) {
          const [user] = data;
          this.userAdded(user);
        }

        return this.isVerifiedUser;
      } finally {
        this.isLoading = false;
      }
    },
    userAdded(data = null) {
      this.$emit("user-added", data);
    },
    /**
     * Verifies the user on the basis of its email address.
     * If user is not present in InboxAware backend Then we create user record in InboxAware
     */
    async verifyAndCreateUserProfile(email) {
      await this.verifyInboxAwareUser(email);

      // Creates user profile if user is not verified in INboxaware
      if (!this.isVerifiedUser) {
        const payload = { user: { email } };
        const { status, data } = await createUserProfile(payload);

        if (isResponseCode(RESPONSE_CODES.success, status)) {
          const { addUserForm } = this.$dictionary.app.manageUsers;
          this.setSnackbar({
            value: true,
            message: addUserForm.successMsg.replace(/<<name>>/gi, email),
            type: this.displayMessages.snackbarTypes.success,
          });
          !data && this.$emit("get-users");
          this.userAdded(data);
        }
      }

      !this.isVerifiedUser && this.$emit("get-users");
    },
    /**
     * Create user
     * @param {Object} params user requets payload details
     * @returns {Promise}
     */
    async createUserProfile({ email, password }) {
      if (!this.userExists && !password) return;

      // Creates user in GIP if user is not present in GIP
      if (!this.userExists && password) await createUser(email, password);

      // Verifies user in InboxAware and then creates its record
      await this.verifyAndCreateUserProfile(email);
      this.closeNavDrawer();
      this.userExists = true;
      this.$refs.form.reset();
    },

    /**
     * Create or Update user
     */
    async handleSubmit() {
      if (!this.isFormValid) return this.$refs.form.validate();
      try {
        this.isLoading = true;

        await this.checkUserAvailability();
        // User Creation
        await this.createUserProfile(this.createUserPayload);
      } catch ({ message, response }) {
        let errorMessage = this.$errorMessage({ message });
        if (!isEmpty(response?.data)) {
          Object.keys(response?.data).forEach((prop) => {
            return (errorMessage = `${capitalize(prop)} ${
              response.data[prop]
            }`);
          });
        }

        this.setSnackbar({
          value: true,
          message: errorMessage,
          type: this.displayMessages.snackbarTypes.error,
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Checks if user exists in the gip and then adds the user to the selected account
     */
    async checkUserAvailability() {
      const userSignInMethod = await fetchSignInMethodsForEmail(
        this.user.email
      );
      this.userExists = !isEmpty(userSignInMethod);

      return this.userExists;
    },
  },
};
</script>
